import {alpha, createTheme} from '@mui/material/styles';
import typography from './typography';
import {buildClassificationCssVars, CLASSIFICATION_COLORS} from './classificationColors';

/**
 * Premium MUI Theme - "Serene"
 *
 * Design Rationale:
 * ─────────────────────────────────────────────────────────────────
 *
 * COLOR PHILOSOPHY:
 * - Primary (Deep Indigo #3B3B6D): Authoritative, focused, professional. Deep navy-indigo
 *   conveys depth and trust. Works well for interactive elements and key actions.
 * - Secondary (Dusty Mauve #8F6B8A): Warm complementary tone that adds personality
 *   without competing with the strong primary.
 * - Neutrals: Cool grays with subtle indigo undertones create depth without
 *   feeling sterile. Background uses layered tones for visual hierarchy.
 * - Semantic colors are desaturated for clarity without alarm fatigue.
 *
 * SURFACE STRATEGY:
 * - Multiple background levels create natural depth and separation
 * - Paper surfaces have subtle warmth to reduce eye strain
 * - Borders are used sparingly; elevation provides hierarchy
 *
 * COMPONENT DESIGN:
 * - Soft 8px border radius is modern but not trendy
 * - Transitions are quick (150-200ms) for responsiveness
 * - Focus states are clear but not jarring
 * - Hover states provide feedback without distraction
 */

// ─────────────────────────────────────────────────────────────────
// Color Palette
// ─────────────────────────────────────────────────────────────────

const palette = {
    // Primary - Deep Indigo (authoritative, focused)
    primary: {
        main: '#3B3B6D',
        light: '#5E5E95',
        dark: '#2A2A52',
        contrastText: '#FFFFFF',
    },
    // Secondary - Dusty Mauve (warm complement to indigo)
    secondary: {
        main: '#8F6B8A',
        light: '#B090AB',
        dark: '#6D4E69',
        contrastText: '#FFFFFF',
    },
    // Semantic colors - muted but clear
    error: {
        main: '#C5616E',
        light: '#E08A94',
        dark: '#A04450',
        contrastText: '#FFFFFF',
    },
    warning: {
        main: '#C9943A',
        light: '#E5B86A',
        dark: '#A67520',
        contrastText: '#1A1D21',
    },
    success: {
        main: '#5A9367',
        light: '#7BB388',
        dark: '#3D7449',
        contrastText: '#FFFFFF',
    },
    info: {
        main: '#5B7FA8',
        light: '#7CA0C8',
        dark: '#3D5E88',
        contrastText: '#FFFFFF',
    },
    // Neutral grays with subtle indigo undertones
    grey: {
        50: '#F7F7FB',
        100: '#EFEFF5',
        200: '#E4E4EE',
        300: '#D5D5E6',
        400: '#C0C0D8',
        500: '#9898B8',
        600: '#72728C',
        700: '#4A4A5E',
        800: '#2E2E40',
        900: '#1A1A28',
    },
};

// ─────────────────────────────────────────────────────────────────
// Light Theme Palette
// ─────────────────────────────────────────────────────────────────

const lightPalette = {
    mode: 'light',
    ...palette,
    // Official US classification banner colors — fixed across light/dark mode.
    classification: CLASSIFICATION_COLORS,
    background: {
        default: '#F5F6F8',      // Subtle warm gray
        paper: '#FFFFFF',
        subtle: '#ECEEF1',       // For cards on default bg
        elevated: '#FFFFFF',     // For elevated surfaces
    },
    text: {
        primary: '#1A1D21',      // Soft black, easier on eyes
        secondary: '#5A6169',    // Muted for secondary content
        disabled: '#9CA3AB',
    },
    divider: alpha('#1A1D21', 0.08),
    action: {
        active: alpha('#1A1D21', 0.56),
        hover: alpha('#1A1D21', 0.04),
        selected: alpha('#3B3B6D', 0.08),
        disabled: alpha('#1A1D21', 0.26),
        disabledBackground: alpha('#1A1D21', 0.08),
        focus: alpha('#3B3B6D', 0.12),
    },
};

// ─────────────────────────────────────────────────────────────────
// Dark Theme Palette
// ─────────────────────────────────────────────────────────────────

const darkPalette = {
    mode: 'dark',
    primary: {
        main: '#8080B8',         // Lighter for dark mode
        light: '#A8A8D0',
        dark: '#5E5E95',
        contrastText: '#0D0D1A',
    },
    secondary: {
        main: '#B090AB',
        light: '#CCADC8',
        dark: '#8F6B8A',
        contrastText: '#0D0D1A',
    },
    error: {
        main: '#E08A94',
        light: '#F5B0B8',
        dark: '#C5616E',
        contrastText: '#0D0D1A',
    },
    warning: {
        main: '#E5B86A',
        light: '#F5D59A',
        dark: '#C9943A',
        contrastText: '#0D0D1A',
    },
    success: {
        main: '#7BB388',
        light: '#A5D3B0',
        dark: '#5A9367',
        contrastText: '#0D0D1A',
    },
    info: {
        main: '#7CA0C8',
        light: '#A5C3E0',
        dark: '#5B7FA8',
        contrastText: '#0D0D1A',
    },
    grey: palette.grey,
    // Official US classification banner colors — fixed across light/dark mode.
    classification: CLASSIFICATION_COLORS,
    background: {
        default: '#0D0D1A',      // Deep indigo-black
        paper: '#14142A',
        subtle: '#1A1A34',
        elevated: '#20203E',
    },
    text: {
        primary: '#E6EDF3',
        secondary: '#8B949E',
        disabled: '#484F58',
    },
    divider: alpha('#E6EDF3', 0.08),
    action: {
        active: alpha('#E6EDF3', 0.56),
        hover: alpha('#E6EDF3', 0.08),
        selected: alpha('#8080B8', 0.16),
        disabled: alpha('#E6EDF3', 0.3),
        disabledBackground: alpha('#E6EDF3', 0.12),
        focus: alpha('#8080B8', 0.2),
    },
};

// ─────────────────────────────────────────────────────────────────
// Shape Configuration
// ─────────────────────────────────────────────────────────────────

const shape = {
    borderRadius: 8, // Soft, modern corners
};

// ─────────────────────────────────────────────────────────────────
// Custom Shadows (softer, more natural)
// ─────────────────────────────────────────────────────────────────

const createShadows = (mode) => {
    const shadowColor = mode === 'dark' ? '0, 0, 0' : '30, 41, 59';
    return [
        'none',
        `0 1px 2px 0 rgba(${shadowColor}, 0.05)`,
        `0 1px 3px 0 rgba(${shadowColor}, 0.08), 0 1px 2px -1px rgba(${shadowColor}, 0.08)`,
        `0 4px 6px -1px rgba(${shadowColor}, 0.08), 0 2px 4px -2px rgba(${shadowColor}, 0.08)`,
        `0 10px 15px -3px rgba(${shadowColor}, 0.08), 0 4px 6px -4px rgba(${shadowColor}, 0.08)`,
        `0 20px 25px -5px rgba(${shadowColor}, 0.08), 0 8px 10px -6px rgba(${shadowColor}, 0.08)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.2)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        // Extended shadows for larger elevations
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
        `0 25px 50px -12px rgba(${shadowColor}, 0.25)`,
    ];
};

// ─────────────────────────────────────────────────────────────────
// Transition Presets
// ─────────────────────────────────────────────────────────────────

const transitions = {
    duration: {
        shortest: 100,
        shorter: 150,
        short: 200,
        standard: 250,
        complex: 300,
        enteringScreen: 200,
        leavingScreen: 150,
    },
    easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
};

// ─────────────────────────────────────────────────────────────────
// Component Overrides
// ─────────────────────────────────────────────────────────────────

const createComponentOverrides = (paletteConfig) => {
    return {
        // ─────────────────────────────────────────────────────────
        // CssBaseline - Global styles
        // ─────────────────────────────────────────────────────────
        MuiCssBaseline: {
            styleOverrides: {
                // Expose classification colors as CSS custom properties so plain
                // .css files (which can't import JS) can stay in sync with
                // theme/classificationColors.js without duplicating values.
                ':root': buildClassificationCssVars(paletteConfig.classification),
                body: {
                    scrollbarColor: '#CED4DA #F5F6F8',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#F5F6F8',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#CED4DA',
                        borderRadius: '4px',
                        '&:hover': {
                            background: '#ADB5BD',
                        },
                    },
                },
                '*': {
                    scrollbarWidth: 'thin',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Buttons
        // ─────────────────────────────────────────────────────────
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: '6px',
                    padding: '8px 16px',
                    transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: `0 2px 8px ${alpha(paletteConfig.primary.main, 0.25)}`,
                    },
                },
                outlined: {
                    borderWidth: '1.5px',
                    '&:hover': {
                        borderWidth: '1.5px',
                        backgroundColor: alpha(paletteConfig.primary.main, 0.04),
                    },
                },
                text: {
                    '&:hover': {
                        backgroundColor: alpha(paletteConfig.primary.main, 0.06),
                    },
                },
                sizeSmall: {
                    padding: '6px 12px',
                    fontSize: '0.8125rem',
                },
                sizeLarge: {
                    padding: '10px 24px',
                    fontSize: '0.9375rem',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // IconButton
        // ─────────────────────────────────────────────────────────
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        backgroundColor: paletteConfig.action.hover,
                    },
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Text Fields & Inputs
        // ─────────────────────────────────────────────────────────
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    backgroundColor: alpha('#000000', 0.02),
                    transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        backgroundColor: alpha('#000000', 0.03),
                    },
                    '&.Mui-focused': {
                        backgroundColor: '#FFFFFF',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: paletteConfig.primary.main,
                            borderWidth: '2px',
                        },
                    },
                },
                notchedOutline: {
                    borderColor: paletteConfig.divider,
                    transition: 'border-color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                    '&.Mui-focused': {
                        color: paletteConfig.primary.main,
                    },
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Select
        // ─────────────────────────────────────────────────────────
        MuiSelect: {
            styleOverrides: {
                select: {
                    borderRadius: '8px',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Cards
        // ─────────────────────────────────────────────────────────
        MuiCard: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    borderRadius: '12px',
                    border: `1px solid ${paletteConfig.divider}`,
                    backgroundColor: paletteConfig.background.paper,
                    transition: 'box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1), border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        borderColor: alpha('#1A1D21', 0.12),
                    },
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    padding: '16px 20px 12px',
                },
                title: {
                    fontSize: '1rem',
                    fontWeight: 600,
                },
                subheader: {
                    fontSize: '0.8125rem',
                    color: paletteConfig.text.secondary,
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '16px 20px',
                    '&:last-child': {
                        paddingBottom: '20px',
                    },
                },
            },
        },
        MuiCardActions: {
            styleOverrides: {
                root: {
                    padding: '12px 20px 16px',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Paper
        // ─────────────────────────────────────────────────────────
        MuiPaper: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    backgroundImage: 'none', // Remove default gradient
                },
                rounded: {
                    borderRadius: '12px',
                },
                outlined: {
                    borderColor: paletteConfig.divider,
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Tables
        // ─────────────────────────────────────────────────────────
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    '& .MuiTableCell-head': {
                        backgroundColor: '#F8F9FA',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: paletteConfig.text.secondary,
                        borderBottom: `1px solid ${paletteConfig.divider}`,
                        padding: '12px 16px',
                    },
                },
            },
        },
        MuiTableBody: {
            styleOverrides: {
                root: {
                    '& .MuiTableRow-root': {
                        '&:hover': {
                            backgroundColor: paletteConfig.action.hover,
                        },
                        '&:last-child .MuiTableCell-root': {
                            borderBottom: 'none',
                        },
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: `1px solid ${paletteConfig.divider}`,
                    padding: '14px 16px',
                    fontSize: '0.875rem',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    transition: 'background-color 100ms cubic-bezier(0.4, 0, 0.2, 1)',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Tabs
        // ─────────────────────────────────────────────────────────
        MuiTabs: {
            styleOverrides: {
                root: {
                    minHeight: '44px',
                },
                indicator: {
                    height: '2.5px',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    padding: '12px',
                    // // fontSize: '0.875rem',
                    // minHeight: '44px',
                    // paddingLeft: 16,
                    // paddingRight: 16,
                    minWidth: 48,
                    transition: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                    '&.Mui-selected': {
                        fontWeight: 600,
                    },
                },
                labelIcon: {
                    minHeight: '44px',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Chips
        // ─────────────────────────────────────────────────────────
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    fontWeight: 500,
                    // fontSize: '0.75rem',
                },
                outlined: {
                    borderColor: paletteConfig.divider,
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Dialogs
        // ─────────────────────────────────────────────────────────
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: '16px',
                    boxShadow: '0 24px 48px -12px rgba(30, 41, 59, 0.18)',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    padding: '20px 24px 12px',
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: '12px 24px 20px',
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '12px 24px 20px',
                    gap: '8px',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Menu & Popover
        // ─────────────────────────────────────────────────────────
        MuiMenu: {
            styleOverrides: {
                paper: {
                    borderRadius: '10px',
                    marginTop: '4px',
                    boxShadow: '0 10px 40px -12px rgba(30, 41, 59, 0.15)',
                    border: `1px solid ${paletteConfig.divider}`,
                },
                list: {
                    padding: '6px',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    transition: 'background-color 100ms cubic-bezier(0.4, 0, 0.2, 1)',
                    margin: '2px 0',
                    '&:hover': {
                        backgroundColor: paletteConfig.action.hover,
                    },
                    '&.Mui-selected': {
                        backgroundColor: paletteConfig.action.selected,
                        '&:hover': {
                            backgroundColor: paletteConfig.action.selected,
                        },
                    },
                },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    borderRadius: '10px',
                    boxShadow: '0 10px 40px -12px rgba(30, 41, 59, 0.15)',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Tooltips
        // ─────────────────────────────────────────────────────────
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#343A40',
                    color: '#F5F6F8',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    padding: '6px 12px',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                },
                arrow: {
                    color: '#343A40',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Alerts
        // ─────────────────────────────────────────────────────────
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: '10px',
                    padding: '12px 16px',
                },
                standardSuccess: {
                    backgroundColor: alpha(paletteConfig.success.main, 0.1),
                    color: paletteConfig.success.dark,
                },
                standardError: {
                    backgroundColor: alpha(paletteConfig.error.main, 0.1),
                    color: paletteConfig.error.dark,
                },
                standardWarning: {
                    backgroundColor: alpha(paletteConfig.warning.main, 0.1),
                    color: paletteConfig.warning.dark,
                },
                standardInfo: {
                    backgroundColor: alpha(paletteConfig.info.main, 0.1),
                    color: paletteConfig.info.dark,
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Snackbar
        // ─────────────────────────────────────────────────────────
        MuiSnackbar: {
            styleOverrides: {
                root: {
                    '& .MuiPaper-root': {
                        borderRadius: '10px',
                    },
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Avatars
        // ─────────────────────────────────────────────────────────
        MuiAvatar: {
            styleOverrides: {
                root: {
                    backgroundColor: paletteConfig.primary.main,
                    color: paletteConfig.primary.contrastText,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Badges
        // ─────────────────────────────────────────────────────────
        MuiBadge: {
            styleOverrides: {
                badge: {
                    fontWeight: 600,
                    fontSize: '0.6875rem',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Lists
        // ─────────────────────────────────────────────────────────
        MuiList: {
            styleOverrides: {
                root: {
                    // padding: '4px',
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    transition: 'background-color 100ms cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        backgroundColor: paletteConfig.action.hover,
                    },
                    '&.Mui-selected': {
                        backgroundColor: paletteConfig.action.selected,
                        '&:hover': {
                            backgroundColor: paletteConfig.action.selected,
                        },
                    },
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Breadcrumbs
        // ─────────────────────────────────────────────────────────
        MuiBreadcrumbs: {
            styleOverrides: {
                root: {
                    fontSize: '0.8125rem',
                },
                separator: {
                    color: paletteConfig.text.disabled,
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Links
        // ─────────────────────────────────────────────────────────
        MuiLink: {
            styleOverrides: {
                root: {
                    textDecoration: 'none',
                    '&:hover': {
                        textDecoration: 'underline',
                    },
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Switches, Checkboxes, Radio
        // ─────────────────────────────────────────────────────────
        MuiSwitch: {
            styleOverrides: {
                root: {
                    width: 44,
                    height: 24,
                    padding: 0,
                },
                switchBase: {
                    padding: 2,
                    '&.Mui-checked': {
                        transform: 'translateX(20px) !important',
                        '& + .MuiSwitch-track': {
                            backgroundColor: paletteConfig.primary.main,
                            opacity: 1,
                        },
                    },
                },
                thumb: {
                    width: 20,
                    height: 20,
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
                    backgroundColor: 'white'
                },
                track: {
                    borderRadius: 12,
                    backgroundColor: '#CED4DA',
                    opacity: 1,
                    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    borderRadius: '4px',
                    transition: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    transition: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Progress
        // ─────────────────────────────────────────────────────────
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    borderRadius: '4px',
                    height: '6px',
                    backgroundColor: alpha('#000000', 0.08),
                },
                bar: {
                    borderRadius: '4px',
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                circle: {
                    strokeLinecap: 'round',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Skeleton
        // ─────────────────────────────────────────────────────────
        MuiSkeleton: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha('#000000', 0.06),
                },
                rounded: {
                    borderRadius: '8px',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Divider
        // ─────────────────────────────────────────────────────────
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: paletteConfig.divider,
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // AppBar & Toolbar
        // ─────────────────────────────────────────────────────────
        MuiAppBar: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    backgroundColor: paletteConfig.background.paper,
                    color: paletteConfig.text.primary,
                    borderBottom: `1px solid ${paletteConfig.divider}`,
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    minHeight: '56px',
                    '@media (min-width: 600px)': {
                        minHeight: '64px',
                    },
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Drawer
        // ─────────────────────────────────────────────────────────
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: paletteConfig.background.paper,
                    borderRight: `1px solid ${paletteConfig.divider}`,
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Accordion
        // ─────────────────────────────────────────────────────────
        MuiAccordion: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    border: `1px solid ${paletteConfig.divider}`,
                    '&:before': {
                        display: 'none',
                    },
                    '&.Mui-expanded': {
                        margin: 0,
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    padding: '0 16px',
                    minHeight: '52px',
                    '&.Mui-expanded': {
                        minHeight: '52px',
                    },
                },
                content: {
                    '&.Mui-expanded': {
                        margin: '12px 0',
                    },
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    padding: '0 16px 16px',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Slider
        // ─────────────────────────────────────────────────────────
        MuiSlider: {
            styleOverrides: {
                root: {
                    height: 6,
                },
                thumb: {
                    width: 16,
                    height: 16,
                    '&:hover': {
                        boxShadow: `0 0 0 8px ${alpha(paletteConfig.primary.main, 0.16)}`,
                    },
                    '&.Mui-focusVisible': {
                        boxShadow: `0 0 0 8px ${alpha(paletteConfig.primary.main, 0.16)}`,
                    },
                },
                track: {
                    borderRadius: 3,
                },
                rail: {
                    borderRadius: 3,
                    opacity: 0.3,
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // ToggleButton
        // ─────────────────────────────────────────────────────────
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: '8px',
                    padding: '8px 16px',
                    '&.Mui-selected': {
                        backgroundColor: paletteConfig.action.selected,
                    },
                },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Autocomplete
        // ─────────────────────────────────────────────────────────
        MuiAutocomplete: {
            styleOverrides: {
                paper: {
                    borderRadius: '10px',
                    marginTop: '4px',
                    boxShadow: '0 10px 40px -12px rgba(30, 41, 59, 0.15)',
                    border: `1px solid ${paletteConfig.divider}`,
                },
                listbox: {
                    padding: '6px',
                },
                option: {
                    borderRadius: '6px',
                    margin: '2px 0',
                    padding: '8px 12px',
                    '&:hover': {
                        backgroundColor: paletteConfig.action.hover,
                    },
                    '&[aria-selected="true"]': {
                        backgroundColor: paletteConfig.action.selected,
                    },
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Stepper
        // ─────────────────────────────────────────────────────────
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    '&.Mui-completed': {
                        color: paletteConfig.success.main,
                    },
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Rating
        // ─────────────────────────────────────────────────────────
        MuiRating: {
            styleOverrides: {
                iconFilled: {
                    color: paletteConfig.warning.main,
                },
            },
        },

        // ─────────────────────────────────────────────────────────
        // Pagination
        // ─────────────────────────────────────────────────────────
        MuiPaginationItem: {
            styleOverrides: {
                root: {
                    borderRadius: '6px',
                    '&.Mui-selected': {
                        fontWeight: 600,
                    },
                },
            },
        },
    };
};

// ─────────────────────────────────────────────────────────────────
// Theme Creation
// ─────────────────────────────────────────────────────────────────

// Light theme (default)
const theme = createTheme({
    palette: lightPalette,
    typography,
    shape,
    shadows: createShadows('light'),
    transitions,
    components: createComponentOverrides(lightPalette),
});

// Dark theme export
export const darkTheme = createTheme({
    palette: darkPalette,
    typography,
    shape,
    shadows: createShadows('dark'),
    transitions,
    components: createComponentOverrides(darkPalette),
});

// ─────────────────────────────────────────────────────────────────
// Optional Tweaks - Theme Modifiers
// ─────────────────────────────────────────────────────────────────

/**
 * Theme Modifiers - Apply these to create variations:
 *
 * MORE PLAYFUL:
 * - Increase border radius to 12-16px
 * - Use more saturated primary/secondary colors
 * - Add subtle gradients to buttons
 * - Warmer background tones
 *
 * MORE ENTERPRISE:
 * - Reduce border radius to 4-6px
 * - Use more muted, desaturated colors
 * - Stricter spacing and typography scale
 * - Remove decorative elements
 *
 * MORE EDITORIAL:
 * - Use serif font for headings (e.g., 'Merriweather')
 * - Increase line height for body text to 1.7-1.8
 * - Reduce max-width for content areas
 * - Add more generous vertical spacing
 *
 * USAGE EXAMPLE:
 *
 * import { createTheme } from '@mui/material/styles';
 * import theme from './theme';
 *
 * // Make it more playful
 * const playfulTheme = createTheme(theme, {
 *     shape: { borderRadius: 14 },
 *     palette: {
 *         primary: { main: '#6366F1' }, // More vibrant purple
 *         secondary: { main: '#F472B6' }, // Pink accent
 *     },
 * });
 */

export default theme;
